import React, { memo, useState } from 'react';
import { Box, CircularProgress, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Button from 'modules/views/shared/Button';

import PiiPinVerificationModals from 'modules/views/shared/PiiPinVerificationModals';
import useProfileInformation from './hooks/useProfileInformation';
import { useStyles } from './styles';
import {
  PersonalInfoSection,
  ContactInfoSection,
  HomeAddressSection,
  EmergencyContactSection,
  GovernmentIdSection,
  WorkerTradesSection,
  SignatureSection,
} from './components';

export const ProfileInformation: React.FC = memo(() => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [showPiiPinVerificationModal, setShowPiiPinVerificationModal] = useState(false);

  const {
    values,
    errors,
    setValue,
    handleSubmit,
    isLoading,
    isConfigLoading,
    isProfilePictureUploading,
    showCaptureModal,
    setShowCaptureModal,
    deleteId,
    governmentDocumentFiles,
    workerId,
    canEditEmail,
    canEditMobilePhoneNumber,
    onUploadProfilePicture,
    onAddGovernmentDocumentFile,
    onDeleteGovernmentDocumentFile,
    onAddressSelect,
    personalInformationFields,
    genderSectionFields,
    profilePhotoField,
    govtIdFields,
    govtIdImageField,
    contactInfoFields,
    addressFields,
    allowedCountryCodes,
    emergencyContactFields,
    emergencyPhoneField,
    roleFields,
    masterListTrades,
    nonMasterListTrades,
    isTradesRequired,
    languageCode,
  } = useProfileInformation();

  return (
    <Box className={classes.profileInformationContainer} data-testid="profile-information-form">
      {isConfigLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={300}
          data-testid="profile-information-loader"
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <PersonalInfoSection
            personalInformationFields={personalInformationFields}
            genderSectionFields={genderSectionFields}
            profilePhotoField={profilePhotoField}
            values={values}
            errors={errors}
            setValue={setValue}
            isProfilePictureUploading={isProfilePictureUploading}
            showCaptureModal={showCaptureModal}
            workerId={workerId}
            onOpenCaptureModal={() => setShowCaptureModal(true)}
            onCloseCaptureModal={() => setShowCaptureModal(false)}
            onUploadProfilePicture={onUploadProfilePicture}
            onPiiFieldClick={() => setShowPiiPinVerificationModal(true)}
          />

          <Divider className={classes.divider} />
          <SignatureSection workerId={workerId} languageCode={languageCode} />

          <Divider className={classes.divider} />

          <ContactInfoSection
            contactInfoFields={contactInfoFields}
            values={values}
            errors={errors}
            setValue={setValue}
            canEditEmail={canEditEmail}
            canEditMobilePhoneNumber={canEditMobilePhoneNumber}
          />

          <Divider className={classes.divider} />

          <HomeAddressSection
            addressFields={addressFields}
            allowedCountryCodes={allowedCountryCodes}
            values={values}
            errors={errors}
            setValue={setValue}
            onAddressSelect={onAddressSelect}
            onPiiFieldClick={() => setShowPiiPinVerificationModal(true)}
          />

          <Divider className={classes.divider} />

          <EmergencyContactSection
            emergencyContactFields={emergencyContactFields}
            emergencyPhoneField={emergencyPhoneField}
            values={values}
            errors={errors}
            setValue={setValue}
          />

          <Divider className={classes.divider} />
          <GovernmentIdSection
            govtIdFields={govtIdFields}
            govtIdImageField={govtIdImageField}
            governmentDocumentFiles={governmentDocumentFiles}
            deleteId={deleteId}
            values={values}
            errors={errors}
            setValue={setValue}
            onAddGovernmentDocumentFile={onAddGovernmentDocumentFile}
            onDeleteGovernmentDocumentFile={onDeleteGovernmentDocumentFile}
          />

          <Divider className={classes.divider} />
          <WorkerTradesSection
            roleFields={roleFields}
            masterListTrades={masterListTrades}
            nonMasterListTrades={nonMasterListTrades}
            values={values}
            errors={errors}
            setValue={setValue}
            isTradesRequired={isTradesRequired}
          />

          {showPiiPinVerificationModal && (
            <PiiPinVerificationModals onClose={() => setShowPiiPinVerificationModal(false)} />
          )}

          <div className={classes.buttonContainer}>
            <Button
              label={t('common.saveChanges')}
              isLoading={isLoading}
              styleClass={classes.saveButton}
              testId="profile-information-save-button"
              onClick={handleSubmit}
            />
          </div>
        </>
      )}
    </Box>
  );
});
